import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CodeIcon from "@mui/icons-material/Code";
import UpdateIcon from "@mui/icons-material/Update";
import BalanceIcon from "@mui/icons-material/Balance";
import PsychologyIcon from "@mui/icons-material/Psychology";

const AiWhyChooseUs = ({ baseConfig }) => {
  const { tertColor, tertHoverColor } = baseConfig.siteBranding;

  // Key advantages with icons and descriptions
  const advantages = [
    {
      title: "Technical Expertise",
      icon: <CodeIcon />,
      description:
        "We understand both the legal and technical aspects of AI systems, with backgrounds in engineering and technology that enable us to comprehend complex AI architectures and algorithmic concepts.",
    },
    {
      title: "Practical Experience",
      icon: <PsychologyIcon />,
      description:
        "We regularly advise clients on the practical deployment of AI across diverse industries, including SaaS, healthcare, finance, unmanned aerial systems (UAS), wireless communications, and IoT.",
    },
    {
      title: "Comprehensive IP Strategies",
      icon: <BalanceIcon />,
      description:
        "We provide comprehensive support for AI-related patent filings, intellectual property monetization strategies, licensing agreements, and technology transfer for a cohesive legal approach.",
    },
    {
      title: "Forward-Thinking Counsel",
      icon: <UpdateIcon />,
      description:
        "We stay ahead of emerging AI regulations, copyright developments for AI-generated content, and data privacy laws, preparing your business for both current and future legal frameworks.",
    },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <CheckCircleOutlineIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          Why Choose Kama Thuo, PLLC for Your AI Legal Needs?
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
          mb: 4,
        }}
      >
        <Typography variant="body1" paragraph>
          Our firm combines deep technical knowledge with specialized legal
          expertise to address the unique challenges presented by artificial
          intelligence technologies. Unlike traditional law firms, we bring both
          engineering backgrounds and legal acumen to the table, allowing us to
          understand the technical nuances of your AI systems while providing
          practical, business-focused legal guidance.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {advantages.map((advantage, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                elevation={1}
                sx={{
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box sx={{ color: tertColor, mr: 1.5 }}>
                      {advantage.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {advantage.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2">
                    {advantage.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 4,
            p: 3,
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            border: `1px solid ${tertColor}33`,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            color={tertColor}
            gutterBottom
          >
            Our Approach to AI Legal Services
          </Typography>

          <Typography variant="body1" paragraph>
            We understand that effective AI legal services require more than
            just knowledge of the law—they demand an appreciation of your
            business objectives and the technical realities of artificial
            intelligence. Our approach integrates legal, technical, and business
            considerations to deliver practical solutions that protect your
            interests while enabling innovation.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", mb: 1 }}>
                <CheckCircleOutlineIcon
                  sx={{ color: tertColor, mr: 1.5, mt: 0.3 }}
                />
                <Typography variant="body2">
                  <strong>Technically-Informed Legal Advice</strong> that
                  accounts for the unique characteristics of AI systems
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", mb: 1 }}>
                <CheckCircleOutlineIcon
                  sx={{ color: tertColor, mr: 1.5, mt: 0.3 }}
                />
                <Typography variant="body2">
                  <strong>Risk-Based Prioritization</strong> that focuses on the
                  most significant legal and business risks first
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", mb: 1 }}>
                <CheckCircleOutlineIcon
                  sx={{ color: tertColor, mr: 1.5, mt: 0.3 }}
                />
                <Typography variant="body2">
                  <strong>Business-Friendly Implementation</strong> of legal
                  requirements to minimize operational disruption
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", mb: 1 }}>
                <CheckCircleOutlineIcon
                  sx={{ color: tertColor, mr: 1.5, mt: 0.3 }}
                />
                <Typography variant="body2">
                  <strong>Proactive Legal Strategy</strong> that anticipates
                  future developments in AI regulation and case law
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiWhyChooseUs;
